/**
 * LLM Proxy Service
 * Client for interacting with Jai1 LLM proxy API
 */
import type { Jai1Config } from '../types/framework.types.js';
import type {
    ModelsResponse,
    UserLimits,
    UsageStats,
    CompletionMessage,
    ChatCompletionChunk,
    ModelWithUsage,
} from '../types/llm.types.js';

export class LlmProxyService {
    private readonly baseUrl: string;
    private readonly apiKey: string;

    constructor(config: Jai1Config) {
        // Remove trailing slash if present
        this.baseUrl = config.apiUrl.replace(/\/$/, '');
        this.apiKey = config.accessKey;
    }

    /**
     * Get the base URL for the LLM proxy (OpenAI-compatible endpoint)
     */
    getBaseUrl(): string {
        return `${this.baseUrl}/v1`;
    }

    /**
     * Get the API key
     */
    getApiKey(): string {
        return this.apiKey;
    }

    /**
     * Fetch available models
     */
    async getModels(): Promise<ModelsResponse> {
        const response = await fetch(`${this.baseUrl}/v1/models`, {
            headers: {
                Authorization: `Bearer ${this.apiKey}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch models: ${response.statusText}`);
        }

        const result = await response.json();
        return result ?? { data: [] };
    }

    /**
     * Fetch user limits
     */
    async getLimits(): Promise<UserLimits> {
        const response = await fetch(`${this.baseUrl}/v1/me/limits`, {
            headers: {
                Authorization: `Bearer ${this.apiKey}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch limits: ${response.statusText}`);
        }

        const result = await response.json();
        return result?.data ?? { effectiveAllowedModels: [], effectiveRateLimits: {} };
    }

    /**
     * Fetch usage statistics
     */
    async getUsage(days = 7): Promise<UsageStats> {
        const response = await fetch(`${this.baseUrl}/v1/me/usage?days=${days}`, {
            headers: {
                Authorization: `Bearer ${this.apiKey}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch usage: ${response.statusText}`);
        }

        const result = await response.json();
        return result ?? { data: [] };
    }

    /**
     * Get models with usage information
     */
    async getModelsWithUsage(): Promise<ModelWithUsage[]> {
        const [modelsRes, limits, usage] = await Promise.all([
            this.getModels(),
            this.getLimits(),
            this.getUsage(1), // Today only
        ]);

        // Use GMT+7 timezone to match API date recording
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' });
        const modelsData = modelsRes.data ?? [];

        return modelsData.map((model) => {
            const allowed = limits.effectiveAllowedModels?.includes(model.id) ?? false;
            const dailyLimit = limits.effectiveRateLimits?.[model.id] ?? limits.effectiveRateLimits?.[model.id.toLowerCase()];

            // Find usage record - try exact match first, then case-insensitive
            const usageData = usage.data ?? [];
            const usageRecord = usageData.find(
                (u) => u.model === model.id && u.date === today
            ) || usageData.find(
                (u) => u.model.toLowerCase() === model.id.toLowerCase() && u.date === today
            );
            const usedToday = usageRecord?.count || 0;

            return {
                ...model,
                allowed,
                dailyLimit,
                usedToday,
            };
        });
    }

    /**
     * Non-streaming chat completion (for translation and complete responses)
     * @returns Complete response content
     */
    async chat(
        messages: CompletionMessage[],
        model: string,
        signal?: AbortSignal
    ): Promise<string> {
        const response = await fetch(`${this.baseUrl}/v1/chat/completions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.apiKey}`,
            },
            body: JSON.stringify({
                model,
                messages,
                stream: false,
            }),
            signal,
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Chat completion failed: ${error}`);
        }

        const result = await response.json();
        return result.choices[0]?.message?.content || '';
    }

    /**
     * Stream chat completion
     * @yields Streamed text chunks
     */
    async *chatStream(
        messages: CompletionMessage[],
        model: string,
        signal?: AbortSignal
    ): AsyncGenerator<string> {
        const response = await fetch(`${this.baseUrl}/v1/chat/completions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.apiKey}`,
            },
            body: JSON.stringify({
                model,
                messages,
                stream: true,
            }),
            signal,
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Chat completion failed: ${error}`);
        }

        if (!response.body) {
            throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                // Append new chunk to buffer
                buffer += decoder.decode(value, { stream: true });

                // Process only complete lines (ending with \n)
                const lines = buffer.split('\n');
                // Keep the last incomplete line in buffer
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine) continue;

                    if (trimmedLine.startsWith('data: ')) {
                        const data = trimmedLine.slice(6);
                        if (data === '[DONE]') {
                            return;
                        }

                        try {
                            const parsed = JSON.parse(data) as ChatCompletionChunk;
                            const content = parsed.choices[0]?.delta?.content;
                            if (content) {
                                yield content;
                            }
                        } catch (e) {
                            // Skip malformed SSE chunk
                        }
                    }
                }
            }

            // Process any remaining content in buffer
            if (buffer.trim()) {
                const trimmedLine = buffer.trim();
                if (trimmedLine.startsWith('data: ')) {
                    const data = trimmedLine.slice(6);
                    if (data !== '[DONE]') {
                        try {
                            const parsed = JSON.parse(data) as ChatCompletionChunk;
                            const content = parsed.choices[0]?.delta?.content;
                            if (content) {
                                yield content;
                            }
                        } catch (e) {
                            // Skip malformed SSE chunk
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }
    }

    /**
     * Generate sample cURL command
     */
    generateCurlSample(model: string): string {
        return `curl ${this.getBaseUrl()}/chat/completions \\
  -H "Authorization: Bearer ${this.apiKey.slice(0, 12)}..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "messages": [
      {"role": "user", "content": "Hello!"}
    ]
  }'`;
    }

    /**
     * Generate full cURL command with actual API key
     */
    generateFullCurlSample(model: string): string {
        return `curl ${this.getBaseUrl()}/chat/completions \\
  -H "Authorization: Bearer ${this.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "messages": [
      {"role": "user", "content": "Hello!"}
    ]
  }'`;
    }
}
